import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/api/axios';
import { getEnv } from '@/lib/env';
import { useAuthStore } from '@/stores/auth.store';

export function useCallListSocket() {
  const queryClient = useQueryClient();
  // Clients view: the monitor socket rejects 'client' tokens — don't connect
  const isClientView = useAuthStore((s) => s.user?.role === 'client');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (isClientView) return;

    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 1000;
    const MAX_DELAY = 16000;

    function connect() {
      const token = getAccessToken();
      if (!token) return;

      const apiUrl = getEnv('VITE_API_URL');
      const apiOrigin = new URL(apiUrl);
      const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiOrigin.host}/listen-ws/${encodeURIComponent(token)}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string);

          if (message.type === 'ready') {
            ws.send(JSON.stringify({ type: 'subscribe_list' }));
            return;
          }

          if (message.type === 'call_list_update') {
            queryClient.invalidateQueries({ queryKey: ['calls'] });
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current), MAX_DELAY);
          reconnectAttemptsRef.current++;
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.close();
        wsRef.current = null;
      }
    };
  }, [queryClient, isClientView]);
}
